import { z } from 'zod';

import { GetEncounterMode } from '../../encounter.js';
import { FeedMessageFormat } from '../../feedMessage.js';
import { GetOrderOption } from '../../order.js';
import { PatientMatchStrictness } from '../../patient.js';
import * as PatientUpload from '../../patientUpload.js';
import { GetJobContextMode, Kind as RpaJobKind, RpaJobSuccessFilter } from '../../rpaJob.js';
import { optionalEnum } from '../../utils.js';
import {
  BaseFlowBlockSchema,
  BaseNotableObjectConfigSchema,
  BlockType,
  ConsumerOfPatientSchema,
  NotableObject,
} from '../base/baseSchema.js';
import { BlockValueTemplateDurationSchema } from '../base/blockValueTemplateDurationSchema.js';
import { ProviderIDType } from '../getProvider/config.js';
import { FilterConfigSchema } from '../utils/filterConfig.js';

export const MAX_JOB_CONTEXT_LIMIT = 25;

export const GetHl7MessageEventConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.Hl7MessageEvent),
});

export const GetGuarantorConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.Guarantor),
  patient: ConsumerOfPatientSchema({ isRequired: true }),
});

export const GetPaymentConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.Payment),
});

export const GetAgentConversationConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.AgentConversation),
});

export const GetReferenceTableConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.ReferenceTable),
  referenceTableId: z.string().uuid(),
  selectedColumns: z.array(z.string()).optional(),
  filterConfig: FilterConfigSchema.optional(),
});

export const GetPatientOrderConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.PatientOrder),
  patientOrderId: z.string().optional(),
  patient: ConsumerOfPatientSchema({ isRequired: false }),
  getOrderOption: optionalEnum(GetOrderOption, GetOrderOption.GetOneById),
});

export const GetProviderConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.Provider),
  providerIdType: z.nativeEnum(ProviderIDType).optional(),
  providerId: z.string(),
});

export const GetTemplateConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.Template),
  templateId: z.string(),
  ehrInformationId: z.string(),
});

export const GetExternalDocumentPacketConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.ExternalDocumentPacket),
  externalDocumentPacketId: z.string(),
});

export const GetEncountersConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.Encounter),
  mode: z.nativeEnum(GetEncounterMode).default(GetEncounterMode.FindAll),
  patient: ConsumerOfPatientSchema({ isRequired: false }),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  encounterId: z.string().optional(),
});

export const GetPatientConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.Patient),
  ehrInformationId: z.string().optional(),
  lastName: z.string().optional(),
  firstName: z.string().optional(),
  mrn: z.string().optional(),
  phoneNumber: z.string().optional(),
  dateOfBirth: z.string().optional(),
  patientMatchStrictness: z
    .nativeEnum(PatientMatchStrictness)
    .optional()
    .default(PatientMatchStrictness.Strict),
});

export const GetPracticeConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.Practice),
  isSelectPracticeIdsEnabled: z.boolean().default(false),
  practiceIds: z.string().optional(),
});

export const GetSftpMessageEventConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.SftpMessageEvent),
});

export const GetX12MessageEventConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.X12MessageEvent),
});

export const GetPatientUploadConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.PatientUpload),
  documentType: z.nativeEnum(PatientUpload.DocumentType).optional(),
  patientInsuranceInformationId: z.string().optional(),
  previsitFormTemplateId: z.string().optional(),
});

export const GetPatientInsuranceInformationConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.PatientInsuranceInformation),
});

export const GetPrimaryCareProviderConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.PrimaryCareProvider),
});

export const GetFeedMessagesConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.FeedMessages),
  source: z.string().optional(),
  limit: z.string().optional(),
  messageFormat: z.nativeEnum(FeedMessageFormat).optional(),
});

export const GetEncounterContactsConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.EncounterContacts),
  encounterId: z.string(),
});

export const GetInsuranceOcrResultsConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.InsuranceOcrResults),
  patientUploadId: z.string().min(1, { message: 'Patient upload ID is required' }),
});

export const GetJobContextConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.JobContext),
  mode: z.nativeEnum(GetJobContextMode).default(GetJobContextMode.FindAll),
  rpaJobId: z.string().optional(),
  jobKind: z.nativeEnum(RpaJobKind).optional(),
  successFilter: z.nativeEnum(RpaJobSuccessFilter).optional(),
  lookbackDuration: BlockValueTemplateDurationSchema().optional(),
  limit: z.string().optional(),
});

export type GetJobContextConfig = z.infer<typeof GetJobContextConfigSchema>;

export const GetAssistantConfigSchema = BaseNotableObjectConfigSchema.extend({
  notableObject: z.literal(NotableObject.Assistant),
  assistantId: z.string(),
});

export const GetNotableObjectConfigSchema = z.discriminatedUnion('notableObject', [
  GetReferenceTableConfigSchema,
  GetPaymentConfigSchema,
  GetAgentConversationConfigSchema,
  GetHl7MessageEventConfigSchema,
  GetGuarantorConfigSchema,
  GetPatientOrderConfigSchema,
  GetProviderConfigSchema,
  GetTemplateConfigSchema,
  GetExternalDocumentPacketConfigSchema,
  GetPatientConfigSchema,
  GetPracticeConfigSchema,
  GetSftpMessageEventConfigSchema,
  GetX12MessageEventConfigSchema,
  GetPatientUploadConfigSchema,
  GetPatientInsuranceInformationConfigSchema,
  GetEncountersConfigSchema,
  GetPrimaryCareProviderConfigSchema,
  GetFeedMessagesConfigSchema,
  GetEncounterContactsConfigSchema,
  GetInsuranceOcrResultsConfigSchema,
  GetJobContextConfigSchema,
  GetAssistantConfigSchema,
]);

export type GetNotableObjectConfig = z.infer<typeof GetNotableObjectConfigSchema>;

export const GetNotableObjectBlockSchema = BaseFlowBlockSchema.extend({
  type: z.literal(BlockType.GetNotableObject),
  config: GetNotableObjectConfigSchema,
});

export type GetNotableObjectBlock = z.infer<typeof GetNotableObjectBlockSchema>;
